import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Event, Team } from '../utils/types';
import { apiGet, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import TeamDiscovery from './TeamDiscovery';
import ParticipantManager from './ParticipantManager';

const EventDetails: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { token, user } = useAuth();

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (eventId && token) {
      loadEvent();
      loadEventTeams();
    }
  }, [eventId, token]);

  const loadEvent = async () => {
    if (!eventId || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const eventData = await apiGet<Event>(`/events/${eventId}`, token);
      setEvent(eventData);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load event');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEventTeams = async () => {
    if (!eventId || !token) return;
    
    setTeamsLoading(true);
    
    try {
      // Use the existing teams endpoint with event_id filter
      const teamsData = await apiGet<Team[]>(`/teams?event_id=${eventId}`, token);
      setTeams(teamsData || []);
      console.log('Loaded teams for event:', eventId, teamsData);
    } catch (err) {
      console.error('Failed to load teams:', err);
      // Set empty array as fallback
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading event...</div>;
  }

  if (error) {
    return (
      <div className="error">
        {error}
        <Link to="/events" className="btn btn-secondary" style={{ marginLeft: '10px' }}>
          Back to Events
        </Link>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Event not found</h3>
        <Link to="/events" className="btn">Back to Events</Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Link to="/events" className="btn btn-secondary">← Back to Events</Link>
      </div>
      
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0, marginBottom: '10px' }}>{event.name}</h1>
            <span className={`badge badge-${event.state}`} style={{ fontSize: '14px' }}>
              {event.state.toUpperCase()}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {isOrganizer && (
              <Link 
                to={`/events/${eventId}/participants`}
                className="btn btn-primary"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                👥 View Participants
              </Link>
            )}
            {isOrganizer && (
              <Link 
                to={`/events/${eventId}/presentation`}
                className="btn btn-success"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                📺 Presentation View
              </Link>
            )}
            {isOrganizer && (
              <Link 
                to={`/events/${eventId}/aws-accounts`}
                className="btn btn-warning"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                ☁️ AWS Accounts
              </Link>
            )}
            {isOrganizer && event.state === 'draft' && (
              <Link 
                to={`/events/${eventId}/edit`}
                className="btn btn-secondary"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                ✏️ Edit Event
              </Link>
            )}
          </div>
        </div>
        
        <p style={{ fontSize: '16px', marginBottom: '20px' }}>{event.description}</p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <h3>📅 Registration Deadline</h3>
            <p>{new Date(event.registration_deadline).toLocaleString()}</p>
          </div>
          <div>
            <h3>📝 Overview Deadline</h3>
            <p>{new Date(event.submission_deadline_overview).toLocaleString()}</p>
          </div>
          <div>
            <h3>🎥 Media Deadline</h3>
            <p>{new Date(event.submission_deadline_media).toLocaleString()}</p>
          </div>
        </div>
        
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
          <h3>📊 Statistics</h3>
          <div style={{ display: 'flex', gap: '30px' }}>
            <div>👥 <strong>{teams.length}</strong> teams</div>
            <div>👤 <strong>{teams.reduce((total, team) => total + (team.member_count || 0), 0)}</strong> participants</div>
            <div>📅 Created {new Date(event.created_at).toLocaleDateString()}</div>
          </div>
        </div>

        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>👥 Teams Participating</h3>
            <Link 
              to={`/create-team?eventId=${eventId}`} 
              className="btn"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              🏗️ Create Team
            </Link>
          </div>
          
          {teamsLoading && (
            <div className="loading">Loading teams...</div>
          )}
          
          {!teamsLoading && teams.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4>No teams have registered yet</h4>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Be the first to create a team for this hackathon!
              </p>
              <Link 
                to={`/create-team?eventId=${eventId}`} 
                className="btn"
              >
                🏗️ Create First Team
              </Link>
            </div>
          )}
          
          {!teamsLoading && teams.length > 0 && (
            <div className="grid">
              {teams.map((team) => (
                <div key={team.team_id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, flex: 1, marginRight: '10px' }}>
                      <Link 
                        to={`/teams/${team.team_id}`}
                        style={{ 
                          textDecoration: 'none', 
                          color: 'inherit',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={team.name} // Show full name on hover
                      >
                        {team.name.length > 20 ? `${team.name.substring(0, 20)}...` : team.name}
                      </Link>
                    </h3>
                    {team.looking_for_members && (
                      <span 
                        style={{
                          background: '#28a745',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}
                      >
                        👋 Looking
                      </span>
                    )}
                  </div>
                  
                  <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                    {team.description && team.description.length > 100 
                      ? `${team.description.substring(0, 100)}...` 
                      : team.description || 'No description provided'
                    }
                  </p>
                  
                  {team.desired_skills && team.desired_skills.length > 0 && (
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                        Looking for:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {team.desired_skills.slice(0, 3).map((skill) => (
                          <span 
                            key={`${team.team_id}-${skill}`}
                            style={{
                              background: '#e9ecef',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              color: '#495057'
                            }}
                          >
                            {skill}
                          </span>
                        ))}
                        {team.desired_skills.length > 3 && (
                          <span style={{ fontSize: '11px', color: '#666' }}>
                            +{team.desired_skills.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '1px solid #eee'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      👤 {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                      {team.members && team.members.length > 0 && (
                        <div style={{ marginTop: '5px' }}>
                          {team.members.slice(0, 2).map((member, index) => (
                            <div key={member.email || index} style={{ fontSize: '10px' }}>
                              {member.display_name} {member.role === 'creator' && '(Creator)'}
                            </div>
                          ))}
                          {team.members.length > 2 && (
                            <div style={{ fontSize: '10px', color: '#999' }}>
                              +{team.members.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Link 
                      to={`/teams/${team.team_id}`}
                      className="btn"
                      style={{ fontSize: '12px', padding: '5px 10px' }}
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team Discovery Section */}
        <div style={{ marginTop: '30px' }}>
          <TeamDiscovery eventId={eventId!} />
        </div>

        {/* Participant Management Section (Organizers Only) */}
        {isOrganizer && (
          <div style={{ marginTop: '30px' }}>
            <ParticipantManager eventId={eventId!} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetails;
